import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";
import Button from "@mui/material/Button";
import { Box, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { Formik } from "formik";
import * as Yup from "yup";
import { API } from "../../../api/API";
import { toast } from "react-toastify";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Divider from "@mui/material/Divider";
import { appColors } from "../../../constant/constant";

const UpdateAssureModal = ({ show, handleClose, assure, onUpdateSuccess }) => {
  const [structures, setStructures] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [structuresRes] = await Promise.all([API.get("/structures")]);
        setStructures(structuresRes.data.data || []);
      } catch (error) {
        console.error("Erreur lors du chargement des données", error);
        toast.error("Erreur lors du chargement des données initiales");
      }
    };

    fetchData();
  }, []);

  const validationSchema = Yup.object().shape({
    nom: Yup.string().required("Ce champ est requis"),
    prenom: Yup.string().required("Ce champ est requis"),
    tel: Yup.string()
      .required("Ce champ est requis")
      .matches(/^[0-9]+$/, "Doit contenir uniquement des chiffres")
      .min(8, "Doit contenir au moins 8 chiffres"),
    email: Yup.string().email("Email invalide"),
    adresse: Yup.string().required("Ce champ est requis"),
    date_naissance: Yup.date().nullable().required("Ce champ est requis"),

    structure_id: Yup.string().required("Ce champ est requis"),
    type_document_id: Yup.string().required("Ce champ est requis"),
    numero_document_id: Yup.string().required("Ce champ est requis"),
    date_delivre: Yup.date().nullable().required("Ce champ est requis"),
    lieu_delivre: Yup.string().required("Ce champ est requis"),
  });

  const initialValues = {
    nom: assure?.nom || "",
    prenom: assure?.prenom || "",
    tel: assure?.tel || "",
    email: assure?.email || "",
    adresse: assure?.adresse || "",
    date_naissance: assure?.date_naissance
      ? dayjs(assure.date_naissance)
      : null,
    structure_id: assure?.structure_id || "",
    type_document_id: assure?.type_document_id || "",
    numero_document_id: assure?.numero_document_id || "",
    date_delivre: assure?.date_delivre ? dayjs(assure.date_delivre) : null,
    lieu_delivre: assure?.lieu_delivre || "",
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const toastId = toast.loading("Mise à jour en cours...");
    try {
      const formattedValues = {
        ...values,
        date_naissance: values.date_naissance
          ? dayjs(values.date_naissance).format("YYYY-MM-DD")
          : null,
        date_delivre: values.date_delivre
          ? dayjs(values.date_delivre).format("YYYY-MM-DD")
          : null,
      };

      const response = await API.put(`/assures/${assure.id}`, formattedValues);

      if (response.status === 200) {
        toast.update(toastId, {
          render: "Assuré mis à jour avec succès!",
          type: "success",
          isLoading: false,
          autoClose: 5000,
        });
        onUpdateSuccess?.();
        handleClose();
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Erreur lors de la mise à jour";
      toast.update(toastId, {
        render: errorMessage,
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      aria-labelledby="update-assure-modal"
      centered
      backdrop="static"
    >
      <Modal.Header closeButton className="bg-success text-light">
        <Typography variant="h6" component="h2" color="inherit">
          Modifier l'assuré
        </Typography>
      </Modal.Header>

      <Modal.Body>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            isSubmitting,
          }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <Divider sx={{ color: appColors.secondary }}>
                Informations personnelles
              </Divider>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Nom <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      name="nom"
                      value={values.nom}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.nom && !!errors.nom}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.nom}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Prénom <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      name="prenom"
                      value={values.prenom}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.prenom && !!errors.prenom}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.prenom}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Téléphone <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      name="tel"
                      value={values.tel}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.tel && !!errors.tel}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.tel}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.email && !!errors.email}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Date de naissance <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={values.date_naissance}
                        onChange={(date) =>
                          setFieldValue("date_naissance", date)
                        }
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error:
                              touched.date_naissance && !!errors.date_naissance,
                            helperText:
                              touched.date_naissance && errors.date_naissance,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Adresse <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      name="adresse"
                      value={values.adresse}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.adresse && !!errors.adresse}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.adresse}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Form.Group as={Col} md={6} className="mb-3">
                  <Form.Label>
                    Structure <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <Form.Select
                    name="structure_id"
                    value={values.structure_id}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.structure_id && !!errors.structure_id}
                    required
                    className="rounded-input"
                  >
                    <option value="">Sélectionnez une structure</option>
                    {structures.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.intitule}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.structure_id}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              <Divider sx={{ color: appColors.secondary, mt: 3, mb: 3 }}>
                Informations du document d'identification
              </Divider>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Type de document <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      as="select"
                      name="type_document_id"
                      value={values.type_document_id}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={
                        touched.type_document_id && !!errors.type_document_id
                      }
                    >
                      <option value="">Sélectionnez un type</option>
                      <option value="passport">Passport</option>
                      <option value="cni">Carte d'identité</option>
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">
                      {errors.type_document_id}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Numéro de document <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      name="numero_document_id"
                      value={values.numero_document_id}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={
                        touched.numero_document_id &&
                        !!errors.numero_document_id
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.numero_document_id}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Date de délivrance <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={values.date_delivre}
                        onChange={(date) => setFieldValue("date_delivre", date)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error:
                              touched.date_delivre && !!errors.date_delivre,
                            helperText:
                              touched.date_delivre && errors.date_delivre,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Lieu de délivrance <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      name="lieu_delivre"
                      value={values.lieu_delivre}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.lieu_delivre && !!errors.lieu_delivre}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.lieu_delivre}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  mt: 3,
                }}
              >
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleClose}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    padding: "6px 16px",
                  }}
                >
                  Annuler
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  color="success"
                  disabled={isSubmitting}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    padding: "6px 24px",
                    backgroundColor: "#37B049",
                    "&:hover": { backgroundColor: "#2e8b3a" },
                  }}
                >
                  {isSubmitting ? "Mise à jour..." : "Mettre à jour"}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

UpdateAssureModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  assure: PropTypes.object,
  onUpdateSuccess: PropTypes.func,
};

export default UpdateAssureModal;
